import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { t, productName, productDescription } from "../lib/i18n";
import { Sparkles, Plus, Minus, ShoppingBag, X, Star, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Fallback Stripe-URLer per forsikringstype – brukes hvis backend ikke har
// `stripe_payment_url` på produktet (f.eks. legacy DB-rader fra før integrasjonen).
const STRIPE_FALLBACK = {
  deposit: "https://buy.stripe.com/9B6fZh8jH6itelXb5UcEw00",
  waiver: "https://buy.stripe.com/28EcN51VjeOZelX5LAcEw01",
};
const stripeUrlFor = (product) =>
  product?.stripe_payment_url || STRIPE_FALLBACK[product?.insurance_type] || null;
const stripeUrlForItem = (item) =>
  item?.stripe_payment_url || STRIPE_FALLBACK[item?.insurance_type] || null;

function nights(booking) {
  try {
    const sd = new Date(booking.start_date.slice(0, 10));
    const ed = new Date(booking.end_date.slice(0, 10));
    return Math.max(1, Math.round((ed - sd) / 86400000));
  } catch {
    return 1;
  }
}

function priceTag(p, lang, n) {
  const unit = p.unit_price_eur;
  if (p.type === "rental_per_day") {
    return `${unit} € ${t(lang, "extras_per_day")} · ${unit * n} € total`;
  }
  if (p.type === "rental_per_booking") {
    return `${unit} € ${t(lang, "extras_per_booking")}`;
  }
  return `${unit} € (${t(lang, "extras_purchase")})`;
}

export default function CustomerExtras({ lang, booking, onOrderPlaced, variant = "all" }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});  // {product_id: quantity}
  const [busy, setBusy] = useState(false);
  const [busyInsurance, setBusyInsurance] = useState(false);
  const n = booking ? nights(booking) : 1;

  const loadOrders = async () => {
    if (!booking) return;
    try {
      const r = await api.get("/orders", { params: { booking_id: booking.id } });
      setOrders(r.data || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    loadOrders();
  }, [booking?.id]);

  // Split products by category so insurance gets its own required-choice block.
  const insuranceProducts = products.filter((p) => p.category === "insurance");
  const regularProducts = products.filter((p) => p.category !== "insurance");

  // Detect if customer has already chosen insurance for this booking.
  const insuranceOrder = orders.find(
    (o) => o.status !== "cancelled" && (o.items || []).some((i) => i.category === "insurance"),
  );
  const chosenInsuranceItem = insuranceOrder
    ? (insuranceOrder.items || []).find((i) => i.category === "insurance")
    : null;
  // Slå opp originalt produkt for å hente Stripe-URL hvis vi vil tilby Pay now
  // på en allerede valgt forsikring (avbrutt Stripe-flyt, gammel pay_on_arrival, osv.)
  const chosenInsuranceProduct = chosenInsuranceItem
    ? insuranceProducts.find((p) => p.id === chosenInsuranceItem.product_id)
    : null;

  const payExistingOrderViaStripe = () => {
    if (!insuranceOrder) return;
    const stripeUrl = stripeUrlFor(chosenInsuranceProduct) || stripeUrlForItem(chosenInsuranceItem);
    if (!stripeUrl) return;
    localStorage.setItem("cbt:pending_stripe_order", insuranceOrder.id);
    const sep = stripeUrl.includes("?") ? "&" : "?";
    window.location.href = `${stripeUrl}${sep}client_reference_id=${encodeURIComponent(insuranceOrder.id)}`;
  };

  const addInsurance = async (product) => {
    if (!booking) return;
    setBusyInsurance(true);
    try {
      const stripeUrl = stripeUrlFor(product);
      const intent = stripeUrl ? "pay_now" : "pay_on_arrival";
      const resp = await api.post("/orders", {
        booking_id: booking.id,
        items: [{ product_id: product.id, quantity: 1 }],
        payment_intent: intent,
      });
      // Hvis vi har en Stripe-betalingslenke, send kunden direkte dit.
      if (stripeUrl) {
        const orderId = resp.data?.id;
        if (orderId) {
          localStorage.setItem("cbt:pending_stripe_order", orderId);
        }
        const sep = stripeUrl.includes("?") ? "&" : "?";
        window.location.href = `${stripeUrl}${sep}client_reference_id=${encodeURIComponent(orderId)}`;
        return;
      }
      toast.success(t(lang, "extras_order_placed"));
      await loadOrders();
      onOrderPlaced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusyInsurance(false);
    }
  };

  // Sjekk om vi kommer tilbake fra Stripe – ?stripe_session=cs_... i URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("stripe_session");
    if (!sessionId) return;
    const orderId = localStorage.getItem("cbt:pending_stripe_order");
    if (!orderId) {
      // Rens URL-en uansett så vi ikke prøver igjen
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    (async () => {
      try {
        const r = await api.post("/orders/stripe/confirm", {
          session_id: sessionId, order_id: orderId,
        });
        if (r.data?.marked_paid || r.data?.already_paid) {
          toast.success(lang === "no" ? "Betaling bekreftet ✓" : "Payment confirmed ✓");
        } else if (r.data?.payment_status) {
          toast.error(lang === "no"
            ? `Betalingsstatus: ${r.data.payment_status}`
            : `Payment status: ${r.data.payment_status}`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Stripe confirm error");
      } finally {
        localStorage.removeItem("cbt:pending_stripe_order");
        window.history.replaceState({}, "", window.location.pathname);
        await loadOrders();
        onOrderPlaced?.();
      }
    })();
  }, []);

  // Polling: oppdater ordrer hvert 15. sek så Stripe-betaling vises som "Betalt"
  // automatisk når webhook har bekreftet betalingen.
  useEffect(() => {
    if (!booking) return;
    const id = setInterval(loadOrders, 15000);
    return () => clearInterval(id);
  }, [booking?.id]);

  const setQty = (pid, qty) => {
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[pid];
      else next[pid] = qty;
      return next;
    });
  };

  const lineTotal = (p, q) => {
    const mul = p.type === "rental_per_day" ? n : 1;
    return p.unit_price_eur * q * mul;
  };

  const subtotal = regularProducts.reduce((s, p) => {
    const q = cart[p.id] || 0;
    return q ? s + lineTotal(p, q) : s;
  }, 0);

  const placeOrder = async () => {
    if (!booking) return;
    const items = Object.entries(cart).map(([product_id, quantity]) => ({
      product_id, quantity,
    }));
    if (items.length === 0) return;
    setBusy(true);
    try {
      await api.post("/orders", {
        booking_id: booking.id,
        items,
        payment_intent: "pay_on_arrival",
      });
      toast.success(t(lang, "extras_order_placed"));
      setCart({});
      await loadOrders();
      onOrderPlaced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (products.length === 0) return null;
  const cartCount = Object.values(cart).reduce((s, q) => s + q, 0);

  const insuranceCopy = lang === "no" ? {
    title: "Forsikring – velg ett alternativ",
    subtitle: "Du må velge ett av de to alternativene nedenfor før avgang. Det dekker fører ved evt. skader underveis.",
    required: "Påkrevd",
    chosen: "Forsikring valgt",
    chosen_paid: "Betalt via Stripe",
    chosen_pending_stripe: "Avventer Stripe-betaling – siden oppdateres automatisk når betalingen er gjennomført.",
    chosen_body: (label) => `Du har valgt: ${label}. Betales ved ankomst dersom det ikke er betalt på forhånd via Stripe eller annen løsning.`,
    add: "Legg til",
    pay_card: "Betal med kort",
    pay_arrival: "Betal ved ankomst",
    deposit_fee_title: "4 % kortgebyr ved depositum",
    deposit_fee_body: "Depositum betalt med kort får et betalingsgebyr på 4 %. Vi tar imot kontant depositum ved ankomst om du ønsker å unngå gebyret – depositumet betales tilbake i sin helhet etter utsjekk.",
    deposit_fee_short: "+ 4 % kortgebyr · kontant uten gebyr",
  } : {
    title: "Insurance – choose one option",
    subtitle: "You must choose one of the two options below before departure. It covers the skipper for any damages during the trip.",
    required: "Required",
    chosen: "Insurance selected",
    chosen_paid: "Paid via Stripe",
    chosen_pending_stripe: "Awaiting Stripe payment – this page will update automatically once payment is confirmed.",
    chosen_body: (label) => `You have chosen: ${label}. Payable on arrival unless already paid via Stripe or another method.`,
    add: "Add",
    pay_card: "Pay by card",
    pay_arrival: "Pay on arrival",
    deposit_fee_title: "4% card fee on deposit",
    deposit_fee_body: "A 4% payment fee applies when the deposit is paid by card. We also accept a cash deposit on arrival if you prefer to avoid the fee – the deposit is fully refunded after check-out.",
    deposit_fee_short: "+ 4% card fee · cash accepted with no fee",
  };

  return (
    <>
      {/* Insurance – mandatory choice */}
      {variant !== "extras" && insuranceProducts.length > 0 && (
        <Card
          className={`p-5 border-l-4 ${chosenInsuranceItem ? "border-l-emerald-500" : "border-l-red-500"}`}
          data-testid="customer-insurance"
        >
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-[#A57F39]" />
            <h2 className="text-xl font-bold text-[#0C1824]">{insuranceCopy.title}</h2>
            {!chosenInsuranceItem && (
              <Badge className="bg-red-600 text-white text-[10px]">
                <AlertTriangle className="w-3 h-3 mr-0.5" />
                {insuranceCopy.required}
              </Badge>
            )}
          </div>
          <p className="text-sm text-[#2B3F4D] mb-4">{insuranceCopy.subtitle}</p>

          {chosenInsuranceItem ? (
            <div
              className="p-4 rounded-md bg-emerald-50 border border-emerald-200"
              data-testid="customer-insurance-chosen"
            >
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <ShieldCheck className="w-4 h-4" />
                {insuranceCopy.chosen}
                {insuranceOrder?.status === "paid" && (
                  <Badge className="bg-emerald-600 text-white text-[10px] ml-1" data-testid="insurance-paid-badge">
                    {insuranceCopy.chosen_paid}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-emerald-900 mt-1">
                {insuranceCopy.chosen_body(
                  lang === "no" ? chosenInsuranceItem.name_no : chosenInsuranceItem.name_en,
                )}
              </p>
              <p className="text-xs font-mono text-emerald-700 mt-1.5">
                {chosenInsuranceItem.line_total_eur} €
              </p>
              {insuranceOrder?.status !== "paid" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    onClick={payExistingOrderViaStripe}
                    data-testid="insurance-pay-now-stripe"
                    className="bg-[#635BFF] hover:bg-[#4F46E5] text-white"
                    size="sm"
                  >
                    {lang === "no" ? "Betal nå med kort" : "Pay now by card"}
                  </Button>
                  <span className="text-xs text-[#2B3F4D]">
                    {lang === "no"
                      ? "eller betal ved ankomst"
                      : "or pay on arrival"}
                  </span>
                </div>
              )}
              {insuranceOrder?.status !== "paid"
                && chosenInsuranceItem?.insurance_type === "deposit" && (
                <div
                  className="mt-3 rounded-md border border-amber-400/60 bg-amber-50 p-3"
                  data-testid="deposit-fee-notice-chosen"
                >
                  <div className="flex items-center gap-1.5 text-amber-800 font-semibold text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    {insuranceCopy.deposit_fee_title}
                  </div>
                  <p className="text-xs text-amber-900 mt-1 leading-relaxed">
                    {insuranceCopy.deposit_fee_body}
                  </p>
                </div>
              )}
              {insuranceOrder?.status !== "paid" && insuranceOrder?.payment_intent === "pay_now" && (
                <p
                  className="text-xs text-[#A57F39] mt-2 italic"
                  data-testid="insurance-pending-stripe"
                >
                  {insuranceCopy.chosen_pending_stripe}
                </p>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {insuranceProducts.map((p) => (
                <div
                  key={p.id}
                  data-testid={`insurance-product-${p.insurance_type}`}
                  className={`p-4 rounded-md border ${
                    p.recommended ? "border-[#BD984D] bg-[#BD984D]/5" : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[#0C1824]">{productName(p, lang)}</div>
                    {p.recommended && (
                      <Badge className="bg-[#BD984D] text-white text-[10px]">
                        <Star className="w-3 h-3 mr-0.5" />
                        {t(lang, "extras_recommended")}
                      </Badge>
                    )}
                  </div>
                  {productDescription(p, lang) && (
                    <div className="text-xs text-[#2B3F4D] mt-1.5 leading-relaxed">
                      {productDescription(p, lang)}
                    </div>
                  )}
                  {p.insurance_type === "deposit" && (
                    <div
                      className="mt-2 rounded-md border border-amber-400/60 bg-amber-50 p-2.5"
                      data-testid={`deposit-fee-notice-${p.id}`}
                    >
                      <div className="flex items-center gap-1.5 text-amber-800 font-semibold text-xs">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {insuranceCopy.deposit_fee_title}
                      </div>
                      <p className="text-[11px] text-amber-900 mt-0.5 leading-snug">
                        {insuranceCopy.deposit_fee_body}
                      </p>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-lg font-bold text-[#0C1824]">{p.unit_price_eur} €</div>
                      {p.insurance_type === "deposit" && (
                        <div className="text-[10px] text-amber-800 mt-0.5">
                          {insuranceCopy.deposit_fee_short}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => addInsurance(p)}
                      disabled={busyInsurance}
                      data-testid={`insurance-add-${p.insurance_type}`}
                      className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                      size="sm"
                    >
                      {stripeUrlFor(p) ? insuranceCopy.pay_card : insuranceCopy.add}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {variant !== "insurance" && regularProducts.length > 0 && (
    <Card className="p-5" data-testid="customer-extras">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-[#BD984D]" />
        <h2 className="text-xl font-bold text-[#0C1824]">{t(lang, "extras_title")}</h2>
      </div>
      <p className="text-sm text-[#2B3F4D] mb-4">{t(lang, "extras_subtitle")}</p>

      <div className="space-y-3">
        {regularProducts.map((p) => {
          const q = cart[p.id] || 0;
          return (
            <div
              key={p.id}
              data-testid={`extra-product-${p.id}`}
              className={`p-3 rounded-md border ${
                p.recommended
                  ? "border-[#BD984D]/40 bg-[#BD984D]/5"
                  : "border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0C1824] flex items-center gap-2 flex-wrap">
                    {productName(p, lang)}
                    {p.recommended && (
                      <Badge className="bg-[#BD984D] text-white text-[10px]">
                        <Star className="w-3 h-3 mr-0.5" />
                        {t(lang, "extras_recommended")}
                      </Badge>
                    )}
                  </div>
                  {productDescription(p, lang) && (
                    <div className="text-xs text-[#2B3F4D] mt-1">
                      {productDescription(p, lang)}
                    </div>
                  )}
                  <div className="text-xs font-mono text-[#A57F39] mt-1.5">
                    {priceTag(p, lang, n)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQty(p.id, Math.max(0, q - 1))}
                    data-testid={`extra-qty-down-${p.id}`}
                    className="h-9 w-9 p-0 border-slate-300"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </Button>
                  <div
                    className="w-8 text-center font-mono text-sm"
                    data-testid={`extra-qty-${p.id}`}
                  >
                    {q}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQty(p.id, q + 1)}
                    data-testid={`extra-qty-up-${p.id}`}
                    className="h-9 w-9 p-0 border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {cartCount > 0 && (
        <div className="mt-5 p-4 bg-[#0C1824] text-white rounded-md" data-testid="extras-cart-summary">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.22em] text-[#ECC974]">
              {t(lang, "extras_subtotal")}
            </span>
            <span className="text-2xl font-bold" data-testid="extras-subtotal">{subtotal} €</span>
          </div>
          <Button
            onClick={placeOrder}
            disabled={busy}
            data-testid="extras-place-order"
            className="w-full bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            {t(lang, "extras_order_now")} ({cartCount})
          </Button>
          <p className="text-[11px] text-slate-300 mt-2 text-center">
            {t(lang, "account_pay_at_checkin")}
          </p>
        </div>
      )}
    </Card>
      )}
    </>
  );
}
